import { useEffect, useMemo, useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import {
  searchCharacterVoiceCatalogue,
  type CharacterVoiceCatalogueEntry,
} from "../api/client";
import { createNadDebug } from "../utils/debugLog";

const log = createNadDebug("dw:character-voice-catalogue");
const PAGE_SIZE = 20;

export interface ElevenLabsVoiceCatalogueState {
  voices: CharacterVoiceCatalogueEntry[];
  query: string;
  language: string;
  gender: string;
  age: string;
  accent: string;
  isLoading: boolean;
  error: string | null;
  hasMore: boolean;
  setQuery: (value: string) => void;
  setLanguage: (value: string) => void;
  setGender: (value: string) => void;
  setAge: (value: string) => void;
  setAccent: (value: string) => void;
  loadMore: () => void;
  refetch: () => void;
}

export function useElevenLabsVoiceCatalogue(
  enabled: boolean,
): ElevenLabsVoiceCatalogueState {
  const [query, setQuery] = useState("");
  const [language, setLanguage] = useState("");
  const [gender, setGender] = useState("");
  const [age, setAge] = useState("");
  const [accent, setAccent] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [debouncedAccent, setDebouncedAccent] = useState("");
  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedQuery(query.trim());
    }, 300);
    return () => window.clearTimeout(timeoutId);
  }, [query]);
  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedAccent(accent.trim());
    }, 300);
    return () => window.clearTimeout(timeoutId);
  }, [accent]);
  const normalizedQuery = debouncedQuery;
  const normalizedLanguage = language.trim();
  const normalizedGender = gender.trim();
  const normalizedAge = age.trim();
  const normalizedAccent = debouncedAccent;

  const catalogueQuery = useInfiniteQuery({
    queryKey: [
      "character-voice-catalogue",
      normalizedQuery,
      normalizedLanguage,
      normalizedGender,
      normalizedAge,
      normalizedAccent,
    ],
    initialPageParam: 1,
    queryFn: ({ pageParam }) => {
      const page = Number(pageParam);
      log({
        event: "search",
        trace_id: `character-voice-catalogue:search:${page}`,
        has_query: normalizedQuery.length > 0,
        language: normalizedLanguage,
        gender: normalizedGender,
        age: normalizedAge,
        has_accent: normalizedAccent.length > 0,
        page,
      });
      return searchCharacterVoiceCatalogue({
        query: normalizedQuery,
        language: normalizedLanguage,
        gender: normalizedGender,
        age: normalizedAge,
        accent: normalizedAccent,
        page,
        page_size: PAGE_SIZE,
      });
    },
    getNextPageParam: (lastPage) => lastPage.next_page ?? undefined,
    enabled,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const voices = useMemo(
    () => catalogueQuery.data?.pages.flatMap((page) => page.voices) ?? [],
    [catalogueQuery.data],
  );
  const hasMore = catalogueQuery.hasNextPage;

  const resetQuery = (value: string) => {
    setQuery(value);
  };
  const resetLanguage = (value: string) => {
    setLanguage(value);
  };

  return {
    voices,
    query,
    language,
    gender,
    age,
    accent,
    isLoading: catalogueQuery.isLoading || catalogueQuery.isFetching,
    error:
      catalogueQuery.error instanceof Error
        ? catalogueQuery.error.message
        : null,
    hasMore,
    setQuery: resetQuery,
    setLanguage: resetLanguage,
    setGender,
    setAge,
    setAccent,
    loadMore: () => {
      if (hasMore && !catalogueQuery.isFetchingNextPage) {
        void catalogueQuery.fetchNextPage();
      }
    },
    refetch: () => {
      void catalogueQuery.refetch();
    },
  };
}

export type CharacterVoiceCatalogueState = ElevenLabsVoiceCatalogueState;

export const useCharacterVoiceCatalogue = useElevenLabsVoiceCatalogue;
